import { useCallback, useEffect, useRef, useState } from 'react'
import { getPodcastEpisode } from '@domain/features/podcasts/podcasts-feature'
import type { PodcastEpisode } from '@domain/models/podcast'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import {
  detailEntityMatchesRoute,
  isDetailLoadErrorFatal,
  keepDetailEntityForRoute,
} from './detail-load-state'

function matchesPodcastRoute(episode: PodcastEpisode, episodeId: string): boolean {
  return episode.uid === episodeId
}

export function usePodcast(episodeId: string | undefined) {
  const ssrEpisode = useSsrPageData()?.podcastDetail?.episode ?? null
  const hasMatchingSsrEpisode = detailEntityMatchesRoute(
    ssrEpisode,
    episodeId,
    matchesPodcastRoute,
  )
  const [episode, setEpisodeState] = useState<PodcastEpisode | null>(() =>
    keepDetailEntityForRoute(ssrEpisode, episodeId, matchesPodcastRoute))
  const [loading, setLoading] = useState(!hasMatchingSsrEpisode)
  const [error, setError] = useState<string | null>(null)
  const episodeRef = useRef<PodcastEpisode | null>(episode)

  const setEpisode = useCallback((nextEpisode: PodcastEpisode | null) => {
    episodeRef.current = nextEpisode
    setEpisodeState(nextEpisode)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      await Promise.resolve()
      if (cancelled) {
        return
      }

      if (!episodeId) {
        setEpisode(null)
        setError(null)
        setLoading(false)
        return
      }
      const resolvedEpisodeId = episodeId
      const ssrFallback = keepDetailEntityForRoute(
        ssrEpisode,
        resolvedEpisodeId,
        matchesPodcastRoute,
      )
      if (ssrFallback && !detailEntityMatchesRoute(
        episodeRef.current,
        resolvedEpisodeId,
        matchesPodcastRoute,
      )) {
        setEpisode(ssrFallback)
      } else if (!detailEntityMatchesRoute(
        episodeRef.current,
        resolvedEpisodeId,
        matchesPodcastRoute,
      )) {
        setEpisode(null)
      }

      try {
        if (!detailEntityMatchesRoute(
          episodeRef.current,
          resolvedEpisodeId,
          matchesPodcastRoute,
        )) {
          setLoading(true)
        }
        setError(null)
        const result = await getPodcastEpisode(resolvedEpisodeId)
        if (!cancelled) {
          setEpisode(result)
        }
      } catch (loadError) {
        if (!cancelled && isDetailLoadErrorFatal(
          episodeRef.current,
          resolvedEpisodeId,
          matchesPodcastRoute,
        )) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Failed to load podcast episode.',
          )
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [episodeId, setEpisode, ssrEpisode])

  return { episode, loading, error }
}
